import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { acceptInvite, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Grantee lands here from the SMS invite link:
 *   /permissions/accept?token=...
 * The user must already be authenticated as a Carnaf user (with the same
 * phone the owner addressed the invite to). If not authenticated, we send
 * them through /login first and bring them back here via stored token.
 */
export function AcceptInvitePage() {
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));
  const [status, setStatus] = useState<'pending' | 'success' | 'failure' | 'mismatch' | 'invalid'>('pending');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [vehicleId, setVehicleId] = useState<string | null>(null);

  useEffect(() => {
    const token = search.get('token');
    // A missing or malformed token can be diagnosed before any auth redirect,
    // so the invited user gets a clear "invalid/expired" state with a recovery
    // action instead of silently landing on the bare login screen.
    if (!token || !/^[A-Za-z0-9._-]{16,}$/.test(token)) {
      setStatus('invalid');
      return;
    }

    if (!isAuthed) {
      // Stash the token so the post-verify return path can complete it.
      try {
        localStorage.setItem('carnaf_pending_invite_token', token);
      } catch {
        /* localStorage may be blocked; we still navigate */
      }
      navigate(`/login?next=${encodeURIComponent(`/permissions/accept?token=${token}`)}`, {
        replace: true,
      });
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const result = await acceptInvite(token);
        if (cancelled) return;
        setVehicleId(result.vehicleId);
        setStatus('success');
        // Bring them to the vehicle they just got access to.
        setTimeout(() => navigate(`/kol/vehicles/${result.vehicleId}`, { replace: true }), 1200);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === 'INVITATION_PHONE_MISMATCH') {
          setStatus('mismatch');
          setErrorMessage(err.message);
        } else if (
          err instanceof ApiError &&
          (err.code === 'INVITATION_NOT_FOUND' || err.code === 'INVITATION_EXPIRED')
        ) {
          setStatus('invalid');
        } else {
          setStatus('failure');
          setErrorMessage(err instanceof ApiError ? err.message : t('common.error'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [search, isAuthed, navigate, t]);

  return (
    <div className="min-h-screen bg-carnaf-paper flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === 'pending' && <p className="text-carnaf-ink-muted">{t('accept.pending')}</p>}
        {status === 'success' && (
          <div>
            <h1 className="text-xl text-carnaf-teal-dark font-semibold">{t('accept.success')}</h1>
            {vehicleId && <p className="mt-2 text-sm text-carnaf-ink-muted">{t('accept.redirecting')}</p>}
          </div>
        )}
        {status === 'mismatch' && (
          <div>
            <h1 className="text-xl text-carnaf-red font-semibold mb-2">{t('accept.phone_mismatch_title')}</h1>
            <p className="text-sm text-carnaf-ink-muted mb-4">{t('accept.phone_mismatch_sub')}</p>
            <Link to="/" className="text-carnaf-teal underline">
              {t('accept.back_home')}
            </Link>
          </div>
        )}
        {status === 'invalid' && (
          <div>
            <h1 className="text-xl text-carnaf-red font-semibold mb-2">{t('accept.invalid_title')}</h1>
            <p className="text-base text-carnaf-ink-muted mb-4">{t('accept.invalid_sub')}</p>
            <Link to="/" className="text-carnaf-teal underline">
              {t('accept.back_home')}
            </Link>
          </div>
        )}
        {status === 'failure' && (
          <div>
            <h1 className="text-xl text-carnaf-red font-semibold mb-2">{t('accept.failure')}</h1>
            {errorMessage && <p className="text-base text-carnaf-ink-muted mb-4">{errorMessage}</p>}
            <Link to="/" className="text-carnaf-teal underline">
              {t('accept.back_home')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
